import { Component, HostBinding, ViewChild, ViewEncapsulation } from '@angular/core';
import { CellClickedEvent, RowNode } from 'ag-grid-community';
import * as _ from 'lodash';
import { isBoolean } from '../../../../models/utils';
import { DrRequestInfo } from '../../../../models/dr-request-info';
import { Endpoint, HelpIndex, SettingsType } from '../../../../models/enums';
import { AlignDirection, CellActionType, CellTemplateType, GenericColDef, GridContext, GridRowSelectionType } from '../../../../models/grid-context';
import { ReportGenerationProfile, ReportGenerationProfileEntry, ReportSettingViewData } from '../../../../models/report-settings';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { ObjectActionListType, Permissions, ActionType } from '../../../../models/permission';

@Component({
    standalone: false,
    selector: 'res-reports',
    templateUrl: './report-settings.component.html',
    styleUrls: ['./report-settings.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class ReportSettingsComponent implements SettingsNavigationContentComponent {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;

    // member variables
    gridName = 'Reports Settings';
    automationId = 'report-settings';
    isDirty: boolean;
    validChanges = true;
    handle: string;
    isProject: boolean;
    reportProfile: ReportGenerationProfile;
    reportSettings = new Array<ReportSettingViewData>();
    originalReportSettings = new Array<ReportSettingViewData>();
    rowSelection = GridRowSelectionType.none;
    isBusy: boolean;
    isBusyMessage: string;
    helpIndex: HelpIndex;
    gridContext: GridContext;

    // permissions
    permissionType: ObjectActionListType;
    hasReportsViewOnlyPermission!: boolean;

    reportSelection = GridRowSelectionType.single;
    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Report Name',
            field: 'userFriendlyName',
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.nameIconLabel,
            minWidth: 400,
            lockVisible: true
        },
        {
            headerName: 'Imports',
            field: 'imports',
            colId: 'CORPUS_SET',
            automationId: this.automationId,
            cellTemplateType: this.hasReportsViewOnlyPermission ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
            width: 200,
            minWidth: 100,
            suppressSizeToFit: true,
            headerAlignDirection: AlignDirection.center,
            lockVisible: true
        },
        {
            headerName: 'Data Sets',
            field: 'dataSets',
            colId: 'CORPUS',
            cellTemplateType: this.hasReportsViewOnlyPermission ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
            automationId: this.automationId,
            width: 200,
            minWidth: 100,
            suppressSizeToFit: true,
            headerAlignDirection: AlignDirection.center,
            lockVisible: true
        },
        {
            headerName: 'Project Data',
            field: 'projectData',
            colId: 'WORKING_SET',
            cellTemplateType: this.hasReportsViewOnlyPermission ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
            width: 200,
            minWidth: 100,
            suppressSizeToFit: true,
            headerAlignDirection: AlignDirection.center,
            automationId: this.automationId,
            lockVisible: true
        }
    );

    requestInfo: DrRequestInfo;

    rowClassRules = {
        'disabled-row': () => {
            return this.hasReportsViewOnlyPermission;
        }
    };
    isSystemUser = false;

    // methods
    constructor(private commonService: CommonService, private restService: RestService) { }
    openHelp = () => this.commonService.openHelp(this.helpIndex);

    cellClickedCallback = (rowNode: RowNode, action: CellActionType, event?: CellClickedEvent) => {
        if (rowNode && rowNode.data && action && event) {
            if (action === CellActionType.checkbox) {
                if (this.reportProfile && this.reportProfile.viewData && rowNode.data.reportId && event.colDef.colId && event.colDef.field) {
                    const value = rowNode.data[event.colDef.field];
                    if (isBoolean(value)) {
                        const viewData = this.reportProfile.updateViewData(event.colDef.colId, rowNode.data.reportId, !value);
                        rowNode.setData(viewData);
                        this.dirty();
                    }
                }
            }
        }
    }

    getViewDataAsReportEntries(): any {
        // Initialize the three supported types of report groups/columns
        const allReportEntries = new Array<ReportGenerationProfileEntry>();
        const importsReportEntries = new ReportGenerationProfileEntry('CORPUS_SET');
        importsReportEntries.reports = new Array<string>();
        importsReportEntries.handle = null;
        allReportEntries.push(importsReportEntries);

        const dataSetReportEntries = new ReportGenerationProfileEntry('CORPUS');
        dataSetReportEntries.reports = new Array<string>();
        dataSetReportEntries.handle = null;
        allReportEntries.push(dataSetReportEntries);

        const projectDataReportEntries = new ReportGenerationProfileEntry('WORKING_SET');
        projectDataReportEntries.reports = new Array<string>();
        projectDataReportEntries.handle = null;
        allReportEntries.push(projectDataReportEntries);

        // get the final grid data and update the report entry groups based on report category
        if (this.reportSettings) {
            _.each(this.reportSettings, (reportSettingViewData) => {
                if (reportSettingViewData && reportSettingViewData.imports && reportSettingViewData.imports === true) {
                    if (importsReportEntries && importsReportEntries.reports) {
                        importsReportEntries.reports.push(reportSettingViewData.reportId);
                    }
                }
                if (reportSettingViewData && reportSettingViewData.dataSets && reportSettingViewData.dataSets === true) {
                    if (dataSetReportEntries && dataSetReportEntries.reports) {
                        dataSetReportEntries.reports.push(reportSettingViewData.reportId);
                    }
                }
                if (reportSettingViewData && reportSettingViewData.projectData && reportSettingViewData.projectData === true) {
                    if (projectDataReportEntries && projectDataReportEntries.reports) {
                        projectDataReportEntries.reports.push(reportSettingViewData.reportId);
                    }
                }
            });
        }
        return allReportEntries;
    }

    discardChanges(): void {
        this.isDirty = false;
        this.loadData();
    }

    loadData(): void {
        this.isBusy = true;
        this.isBusyMessage = 'Loading Report Settings...';
        this.isDirty = false;
        this.restService.post(Endpoint.projectManager_getReportSettings, { handle: this.handle, requestHandle: this.handle, systemScope: this.isSystemUser }).then(responseAllFields => {
            this.isBusy = false;
            if (responseAllFields && responseAllFields.requestHandle === this.handle && responseAllFields.reportGenerationProfileDto
                && responseAllFields.reportGenerationProfileDto.reportGenerationProfileEntries) {
                // initialize all the view data related to all the possible reports
                this.reportProfile = new ReportGenerationProfile(responseAllFields);
                // traverse each report category (i.e. Imports, Data Sets, Project Data)
                _.each(responseAllFields.reportGenerationProfileDto.reportGenerationProfileEntries, (reportEntry) => {
                    if (reportEntry.reportCategory && reportEntry.reports) {
                        _.each(reportEntry.reports, (report) => {
                            // this list of reports are only those that have been selected by the user/configured.
                            // So, the report checkbox state is set true.
                            this.reportProfile.updateViewData(reportEntry.reportCategory, report, true);
                        });
                    }
                });
                if (this.reportProfile && this.reportProfile.viewData && (this.reportProfile.viewData.size > 0)) {
                    this.reportSettings = this.reportProfile.getViewDataAsSortedArray();
                    this.originalReportSettings = _.cloneDeep(this.reportSettings);
                    if (this.genericGrid && this.reportSettings) {
                        this.columnDefs.forEach(col => {
                            if (col.headerName.localeCompare('Report Name') !== 0) {
                                col.cellTemplateType = this.hasReportsViewOnlyPermission ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox;
                            }
                        });
                        this.genericGrid.setColumnDefs(this.columnDefs);
                        this.genericGrid.setData(this.reportSettings, true);
                        this.genericGrid.refreshColumns();
                    }
                }
            }
        }, error => {
            this.isBusy = false;
        });
    }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string, settingsType?: SettingsType): void {
        this.handle = projectHandle ? projectHandle : node.data.handle;
        this.isProject = projectHandle !== null;
        this.helpIndex = this.isProject ? HelpIndex.case_reports_manage_settings_dlg : (settingsType === SettingsType.system ? HelpIndex.sys_template_reports_dlg : HelpIndex.org_reports_settings_template_dlg);
        this.isSystemUser = (settingsType === SettingsType.system && (this.commonService.currentUser?.originalCustomerName === 'super_system_customer' || this.commonService.currentUser.importedFromSystemUser));
        this.permissionType = projectHandle ? ObjectActionListType.PRJ_REPORT_SETTINGS : this.isSystemUser ? ObjectActionListType.SYSTEM_REPORTS_SETTINGS : ObjectActionListType.ORG_REPORT_SETTINGS;
        this.hasReportsViewOnlyPermission = !Permissions.checkUserPermission(this.permissionType, ActionType.CREATE);
        this.loadData();
    }

    saveChanges(fromAutoSave = false): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            this.isBusy = true;
            this.isBusyMessage = 'Saving Report Settings...';

            if (this.reportProfile) {
                this.restService.post(Endpoint.projectManager_updateReportSettings, {
                    reportGenerationProfileDto: {
                        defaultProfile: this.reportProfile.defaultProfile,
                        handle: this.reportProfile.handle,
                        name: this.reportProfile.name,
                        projectId: this.reportProfile.projectId,
                        version: this.reportProfile.version,
                        reportGenerationProfileEntries: this.getViewDataAsReportEntries(),
                    },
                    systemScope: this.isSystemUser
                }).then(() => {
                    this.isBusy = false;
                    if (!fromAutoSave) {
                        this.loadData();
                    }
                    resolve(true);
                }, () => {
                    this.isBusy = false;
                    resolve(false);
                });
            }
        });
    }

    isDisabled(): boolean {
        let isDisabled = true;
        if (!this.isBusy && this.isDirty) {
            isDisabled = false;
        }
        return isDisabled;
    }

    dirty(): boolean {
        this.isDirty = false;
        // creates an array of alternating property name, property value
        // and stringify's that array
        const count = this.originalReportSettings.length;
        let originalValue = null;
        let currentValue = null;
        for (let i = 0; i < count; i++) {
            originalValue = this.originalReportSettings[i];
            currentValue = this.reportSettings[i];

            if (currentValue.imports !== originalValue.imports) {
                this.isDirty = true;
                break;
            }

            if (currentValue.dataSets !== originalValue.dataSets) {
                this.isDirty = true;
                break;
            }

            if (currentValue.projectData !== originalValue.projectData) {
                this.isDirty = true;
                break;
            }
        }

        return this.isDirty;
    }
}

